// Lo que el QR guarda en el celular para funcionar sin señal: las áreas con sus tareas,
// los registros hechos sin conexión (se envían solos cuando vuelve) y cuántas áreas
// registró hoy el trabajador.
import api from '../services/api';
import { registroLimpiezaService, REGISTRO_TIMEOUT_MS } from '../services/registroLimpiezaService';
import { Area } from '../types';

const leerJson = <T,>(clave: string, porDefecto: T): T => {
  try {
    const raw = localStorage.getItem(clave);
    return raw ? (JSON.parse(raw) as T) : porDefecto;
  } catch {
    return porDefecto;
  }
};

const guardarJson = (clave: string, valor: unknown) => {
  try { localStorage.setItem(clave, JSON.stringify(valor)); } catch {}
};

const fechaLocal = (d: Date = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// === Áreas ===

export type AreaGuardada = {
  id: number;
  nombre: string;
  pisoNombre?: string;
  estado: string;
  desactivadaManualmente: boolean;
  tareas: { id: number; nombre: string; obligatoria: boolean }[];
};

const claveAreas = (tenantId: number) => `qrAreas:${tenantId}`;

export const aAreaGuardada = (area: Area): AreaGuardada => ({
  id: area.id,
  nombre: area.nombre,
  pisoNombre: area.piso?.nombre,
  estado: area.estado,
  desactivadaManualmente: area.desactivadaManualmente,
  tareas: (area.areaTareas || []).map(at => ({ id: at.tareaId, nombre: at.tareaNombre, obligatoria: at.obligatoria })),
});

export const guardarAreas = (tenantId: number, areas: AreaGuardada[]) => guardarJson(claveAreas(tenantId), areas);

export const leerArea = (tenantId: number, areaId: number): AreaGuardada | null =>
  leerJson<AreaGuardada[]>(claveAreas(tenantId), []).find(a => a.id === areaId) ?? null;

export const actualizarAreaGuardada = (tenantId: number, area: AreaGuardada) =>
  guardarAreas(tenantId, [...leerJson<AreaGuardada[]>(claveAreas(tenantId), []).filter(a => a.id !== area.id), area]);

// Las que más urgen primero: rojas, luego amarillas
export const areasPorLimpiar = (areas: AreaGuardada[], excluirAreaId: number, max = 5) =>
  areas
    .filter(a => a.id !== excluirAreaId && !a.desactivadaManualmente && (a.estado === 'Rojo' || a.estado === 'Amarillo'))
    .sort((a, b) => (a.estado === b.estado ? a.nombre.localeCompare(b.nombre) : a.estado === 'Rojo' ? -1 : 1))
    .slice(0, max);

// === Registros pendientes de envío ===

export type TareaPendiente = { tareaId: number; realizada: boolean; motivo?: string; fechaHoraRealizacion?: string };
export type DetallePendiente = { id: number; realizada: boolean; motivoNoRealizada?: string; fechaHoraRealizacion?: string };

type PendienteBase = { id: string; tenantId: number; usuarioId: number; fechaHoraFin: string; observaciones?: string };

export type RegistroPendiente =
  // Iniciado y terminado sin señal: el servidor aún no lo conoce
  | (PendienteBase & { tipo: 'completo'; areaId: number; fechaHoraInicio: string; tareas: TareaPendiente[] })
  // Iniciado con señal, terminado sin ella: solo falta cerrarlo
  | (PendienteBase & { tipo: 'finalizar'; registroId: number; detalles: DetallePendiente[] });

const CLAVE_PENDIENTES = 'qrRegistrosPendientes';

export const leerPendientes = () => leerJson<RegistroPendiente[]>(CLAVE_PENDIENTES, []);

export const nuevoIdPendiente = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export const agregarPendiente = (pendiente: RegistroPendiente) => guardarJson(CLAVE_PENDIENTES, [...leerPendientes(), pendiente]);

const reemplazarPendiente = (pendiente: RegistroPendiente) =>
  guardarJson(CLAVE_PENDIENTES, leerPendientes().map(p => (p.id === pendiente.id ? pendiente : p)));

const quitarPendiente = (id: string) => guardarJson(CLAVE_PENDIENTES, leerPendientes().filter(p => p.id !== id));

export const contarPendientes = (usuarioId: number) => leerPendientes().filter(p => p.usuarioId === usuarioId).length;

// Rechazos que no se arreglan reintentando (área borrada, datos inválidos). Cualquier otro
// error (sin señal, 5xx, sesión vencida, 403 de suscripción) se reintenta después.
const RECHAZO_DEFINITIVO = [400, 404, 409, 422];

let sincronizando = false;

// Envía en orden los registros guardados sin señal de este usuario y empresa: con el
// token del usuario actual quedarían a su nombre, así que los de otro esperan a que vuelva.
export const sincronizarPendientes = async (usuarioId: number, tenantId: number) => {
  if (sincronizando) return;
  sincronizando = true;
  try {
    for (const pendiente of leerPendientes()) {
      if (pendiente.usuarioId !== usuarioId || pendiente.tenantId !== tenantId) continue;
      try {
        let porCerrar = pendiente;
        if (porCerrar.tipo === 'completo') {
          const res = await api.post('/RegistroLimpieza', {
            areaId: porCerrar.areaId,
            fechaHoraInicio: porCerrar.fechaHoraInicio,
            detallesTareas: porCerrar.tareas.map(t => ({ tareaId: t.tareaId, realizada: false })),
          }, { timeout: REGISTRO_TIMEOUT_MS });
          const detallePorTarea = new Map<number, number>(
            (res.data.detallesTareas || []).map((d: any) => [d.tareaId, d.id] as [number, number])
          );
          porCerrar = {
            id: porCerrar.id,
            tipo: 'finalizar',
            tenantId: porCerrar.tenantId,
            usuarioId: porCerrar.usuarioId,
            fechaHoraFin: porCerrar.fechaHoraFin,
            observaciones: porCerrar.observaciones,
            registroId: res.data.id,
            detalles: porCerrar.tareas
              .filter(t => detallePorTarea.has(t.tareaId))
              .map(t => ({
                id: detallePorTarea.get(t.tareaId) as number,
                realizada: t.realizada,
                motivoNoRealizada: t.realizada ? undefined : t.motivo,
                fechaHoraRealizacion: t.fechaHoraRealizacion,
              })),
          };
          // Ya existe en el servidor: si el cierre falla, reintentar solo el cierre
          reemplazarPendiente(porCerrar);
        }
        if (porCerrar.tipo === 'finalizar') {
          await api.put(`/RegistroLimpieza/${porCerrar.registroId}`, {
            fechaHoraFin: porCerrar.fechaHoraFin,
            observaciones: porCerrar.observaciones,
            detallesTareas: porCerrar.detalles,
          }, { timeout: REGISTRO_TIMEOUT_MS });
        }
        quitarPendiente(pendiente.id);
      } catch (e: any) {
        const status = e?.response?.status;
        if (!RECHAZO_DEFINITIVO.includes(status)) break;
        console.warn('[QR] El servidor rechazó un registro guardado sin señal; se descarta', status, e?.response?.data);
        quitarPendiente(pendiente.id);
      }
    }
  } finally {
    sincronizando = false;
  }
};

// === Áreas registradas hoy ===

const claveConteo = (usuarioId: number) => `qrConteoHoy:${usuarioId}`;

// Respaldo sin señal; con señal manda el conteo del servidor
export const sumarConteoHoy = (usuarioId: number) => {
  const actual = leerJson<{ fecha: string; conteo: number }>(claveConteo(usuarioId), { fecha: '', conteo: 0 });
  const hoy = fechaLocal();
  guardarJson(claveConteo(usuarioId), { fecha: hoy, conteo: actual.fecha === hoy ? actual.conteo + 1 : 1 });
};

export const leerConteoHoy = (usuarioId: number) => {
  const actual = leerJson<{ fecha: string; conteo: number }>(claveConteo(usuarioId), { fecha: '', conteo: 0 });
  return actual.fecha === fechaLocal() ? actual.conteo : 0;
};

// Registros terminados hoy (día local) por el usuario, más los que esperan envío.
// null si no se pudo consultar al servidor.
export const contarRegistrosDeHoy = async (usuarioId: number): Promise<number | null> => {
  const medianoche = new Date();
  medianoche.setHours(0, 0, 0, 0);
  // El servidor filtra por día UTC: se pide desde el día UTC de la medianoche local
  // y se recorta acá
  const res = await registroLimpiezaService.getRegistrosFilteredPaginated(
    1, 200, undefined, usuarioId, 'completados', medianoche.toISOString().slice(0, 10)
  );
  if (!res.success || !res.data) return null;
  const enServidor = res.data.data.filter(r => new Date(r.fechaHoraInicio) >= medianoche).length;
  const hoy = fechaLocal();
  const enEspera = leerPendientes().filter(p => p.usuarioId === usuarioId && fechaLocal(new Date(p.fechaHoraFin)) === hoy).length;
  return enServidor + enEspera;
};
